import React, { useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from 'recharts';

const TASK_COLORS = {
  A: { seizure: '#ef4444', normal: '#10b981' },
  B: { 0: '#3b82f6', 1: '#8b5cf6', 2: '#06b6d4', 3: '#f59e0b', 4: '#ef4444' },
  C: { ied: '#f59e0b', normal: '#10b981' },
};

function formatTick(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function TimelineChart({ results }) {
  const taskLabels = Object.keys(results.tasks || {}).filter(
    (t) => !results.tasks[t].error
  );
  const [selectedTask, setSelectedTask] = useState(taskLabels[0] || 'A');

  const taskData = results.tasks[selectedTask];
  if (!taskData || !taskData.timeline) {
    return <div style={{ color: 'var(--text-muted)', padding: 20 }}>No timeline data</div>;
  }

  // Build chart data
  const chartData = taskData.timeline.map((entry) => {
    const point = { time: entry.start_sec };
    Object.entries(entry.probabilities).forEach(([cls, prob]) => {
      point[cls] = prob;
    });
    return point;
  });

  const classNames = taskData.class_names || [];
  const colors = ['#ef4444', '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b'];
  const threshold = selectedTask === 'A' ? 0.4 : selectedTask === 'C' ? 0.4 : 0.3;

  return (
    <div>
      {taskLabels.length > 1 && (
        <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
          {taskLabels.map((t) => (
            <button
              key={t}
              className={`btn ${selectedTask === t ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '6px 14px', fontSize: '0.8rem' }}
              onClick={() => setSelectedTask(t)}
            >
              Task {t}
            </button>
          ))}
        </div>
      )}

      <div className="timeline-chart">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis
              dataKey="time"
              tickFormatter={formatTick}
              stroke="#64748b"
              fontSize={12}
            />
            <YAxis
              domain={[0, 1]}
              stroke="#64748b"
              fontSize={12}
              tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
            />
            <Tooltip
              contentStyle={{
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: 8,
                color: '#f1f5f9',
              }}
              labelFormatter={(v) => `Time: ${formatTick(v)}`}
              formatter={(v, name) => [`${(v * 100).toFixed(1)}%`, name]}
            />
            <ReferenceLine
              y={threshold}
              stroke="#f59e0b"
              strokeDasharray="5 5"
              label={{ value: `Threshold ${threshold}`, fill: '#f59e0b', fontSize: 11 }}
            />
            {classNames.map((cls, idx) => (
              <Area
                key={cls}
                type="monotone"
                dataKey={cls}
                stroke={colors[idx % colors.length]}
                fill={colors[idx % colors.length]}
                fillOpacity={0.15}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8 }}>
        {classNames.map((cls, idx) => (
          <div key={cls} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
            <div style={{
              width: 12, height: 12, borderRadius: 3,
              background: colors[idx % colors.length]
            }} />
            <span style={{ color: 'var(--text-secondary)' }}>{cls}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
